// Cliente HTTP da API. Sempre envia o cabeçalho anti-CSRF e o cookie de sessão.
import type { ApiErrorBody } from '@impulso/shared'

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message)
  }
}

let onUnauthorized: (() => void) | null = null
/** Chamado quando qualquer rota (exceto /auth/me) responde 401: a sessão acabou. */
export const setUnauthorizedHandler = (fn: () => void) => { onUnauthorized = fn }

async function request<T>(method: string, path: string, body?: unknown, init?: RequestInit): Promise<T> {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'include',
    headers: {
      'x-requested-with': 'impulso',
      ...(body !== undefined && !isForm ? { 'content-type': 'application/json' } : {}),
    },
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    ...init,
  }).catch((err) => {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError(0, 'network', 'Sem conexão com o servidor. Confira sua internet e tente de novo.')
  })
  if (res.status === 204) return undefined as T
  const text = await res.text()
  let data: unknown
  try { data = text ? JSON.parse(text) : undefined } catch { /* resposta que não é JSON (ex.: erro do servidor) */ }
  if (!res.ok) {
    if (res.status === 401 && path !== '/auth/me') onUnauthorized?.()
    const err = (data as ApiErrorBody | undefined)?.error
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? 'Algo deu errado. Tente de novo.', err?.details)
  }
  return data as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, form),
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Algo deu errado. Tente de novo.'
}
